import React from 'react';
import styled from 'styled-components';

import { HorizontalTabs, HorizontalTab } from 'components/core';
import useTranslation from 'hooks/use-translation';
import { ARIA_ROLES } from 'shared/constants';
import { TabLink } from 'src/components/core/tabs/tab-link';
import { RouteParam } from 'src/types';
import { usePrepaidWalletBalance } from 'src/hooks/use-prepaid-wallet-balance';

import { AccountRoutes, AccountTab } from '../account.types';

type AccountTopNavBarProps = {
  accountRoutes: AccountRoutes;
  selectedTab: RouteParam;
  shouldDisplayDutchiePaySection: boolean;
  shouldDisplayLoyaltySection: boolean;
  shouldDisplayReferralsSection: boolean;
  shouldDisplayPrepaidWalletSection: boolean;
};

const ANCHOR_COMPONENT = `a`;

export function AccountTopNavBar(props: AccountTopNavBarProps): JSX.Element {
  const {
    accountRoutes,
    selectedTab,
    shouldDisplayDutchiePaySection,
    shouldDisplayLoyaltySection,
    shouldDisplayReferralsSection,
    shouldDisplayPrepaidWalletSection,
  } = props;
  const { account, preferences, orders, bankAccount, loyalty, referrals, wallet } = accountRoutes;
  const { t } = useTranslation();
  const { formattedBalance: walletBalance } = usePrepaidWalletBalance();

  return (
    <TopNavBarContainer>
      <HorizontalTabs
        aria-label={t('accountPage.navigationTabs', 'My account page navigation tabs')}
        scrollButtons='off'
        selectionFollowsFocus
        value={selectedTab}
        variant='scrollable'
      >
        {shouldDisplayPrepaidWalletSection && wallet && (
          <TabLink value={AccountTab.wallet} href={wallet}>
            <StyledHorizontalTab
              id='account-tab-wallet'
              data-testid='account-tab-wallet'
              aria-controls='account-tab-panel-wallet'
              aria-selected={selectedTab === AccountTab.wallet}
              component={ANCHOR_COMPONENT}
              label={t('accountPage.wallet', 'Wallet · {{balance}}', {
                balance: walletBalance,
              })}
              role={ARIA_ROLES.TAB}
              value={AccountTab.wallet}
            />
          </TabLink>
        )}
        {shouldDisplayDutchiePaySection && bankAccount && (
          <TabLink value={AccountTab.bankAccount} href={bankAccount}>
            <StyledHorizontalTab
              id='account-tab-bank-account'
              data-testid='account-tab-bank-account'
              aria-controls='account-tab-panel-dutchie-pay'
              aria-selected={selectedTab === AccountTab.bankAccount}
              component={ANCHOR_COMPONENT}
              label={t('accountPage.bankAccount', 'Bank Account')}
              role={ARIA_ROLES.TAB}
              value={AccountTab.bankAccount}
            />
          </TabLink>
        )}
        <TabLink value={AccountTab.account} href={account}>
          <StyledHorizontalTab
            id='account-tab-my-account'
            data-testid='account-tab-my-account'
            aria-controls='account-tab-panel-my-account'
            aria-selected={selectedTab === AccountTab.account}
            component={ANCHOR_COMPONENT}
            label={t('accountPage.accountDetails', 'Account Details')}
            role={ARIA_ROLES.TAB}
            value={AccountTab.account}
          />
        </TabLink>
        <TabLink value={AccountTab.orders} href={orders}>
          <StyledHorizontalTab
            id='account-tab-orders'
            data-testid='account-tab-orders'
            aria-controls='account-tab-panel-orders'
            aria-selected={selectedTab === AccountTab.orders}
            component={ANCHOR_COMPONENT}
            label={t('accountPage.orders', 'Orders')}
            role={ARIA_ROLES.TAB}
            value={AccountTab.orders}
          />
        </TabLink>

        {shouldDisplayReferralsSection && referrals && (
          <TabLink value={AccountTab.referrals} href={referrals}>
            <StyledHorizontalTab
              id='account-tab-referrals'
              data-testid='account-tab-referrals'
              aria-controls='account-tab-panel-referrals'
              aria-selected={selectedTab === AccountTab.referrals}
              component={ANCHOR_COMPONENT}
              label={t('accountPage.referrals', 'Referrals')}
              role={ARIA_ROLES.TAB}
              value={AccountTab.referrals}
            />
          </TabLink>
        )}

        {shouldDisplayLoyaltySection && loyalty && (
          <TabLink value={AccountTab.loyalty} href={loyalty}>
            <StyledHorizontalTab
              id='account-tab-loyalty'
              data-testid='account-tab-loyalty'
              aria-controls='account-tab-panel-loyalty'
              aria-selected={selectedTab === AccountTab.loyalty}
              component={ANCHOR_COMPONENT}
              label={t('accountPage.loyalty', 'Loyalty')}
              role={ARIA_ROLES.TAB}
              value={AccountTab.loyalty}
            />
          </TabLink>
        )}

        <TabLink value={AccountTab.preferences} href={preferences}>
          <StyledHorizontalTab
            id='account-tab-preferences'
            data-testid='account-tab-preferences'
            aria-controls='account-tab-panel-preferences'
            aria-selected={selectedTab === AccountTab.preferences}
            component={ANCHOR_COMPONENT}
            label={t('accountPage.preferences', 'Preferences')}
            role={ARIA_ROLES.TAB}
            value={AccountTab.preferences}
          />
        </TabLink>
      </HorizontalTabs>
    </TopNavBarContainer>
  );
}

const TopNavBarContainer = styled.div`
  color: var(--dcm-account-nav-color);
`;

const StyledHorizontalTab = styled(HorizontalTab)`
  &.MuiTab-root {
    white-space: unset;
  }
`;
